import { useEffect, useRef, useState, useCallback } from "react";

import { jwtToken } from "@webmotors/consumer-shared-auth";
import { DEFAULT_TAGS, API_REQUEST_STATUS } from "@/utils/constants";
import type { TConversation } from "@stores/chat-domain.types";

import { useChatStore } from "@stores/chat.store";
import { chatWebSocketService } from "../chat-websocket.service";
import { WS_ACTIONS, WS_FETCH_LIMIT } from "../utils/chat-websocket.constants";
import { notifyHeaderUnreadChanged } from "../utils/chat-websocket.helpers";
import type {
  TConnectionState,
  TPaginationKey,
  TWsMessage,
  TWsGetChatsResponse,
  TWsGetUnreadCountResponse,
  TWsGetMessagesResponse,
  TWsGetPhonesResponse,
  TWsSendMessageResponse,
  TWsUnreadMessageResponse,
  TWsStatusMessageResponse,
  TWsBlockUserResponse,
} from "../chat-websocket.types";
import type {
  TUseChatConnectionConfig,
  TUseChatConnectionReturn,
} from "./use-chat-connection.types";

/** Gerencia o ciclo de vida do WebSocket do chat e registra todos os handlers de mensagens recebidas. */
export function useChatConnection({
  enabled,
}: TUseChatConnectionConfig): TUseChatConnectionReturn {
  const [connectionState, setConnectionState] =
    useState<TConnectionState>("idle");
  const hasConnected = useRef(false);

  const fetchConversations = useCallback(
    (chatType: string, lastKey = undefined, tags: string[] | null = null) => {
      const store = useChatStore.getState();
      if (chatType === "to") {
        store.setConversationListLoading("to", chatType);
      } else {
        store.setConversationListLoading("from", chatType);
      }
      chatWebSocketService.incrementPendingRequestCount(chatType);
      chatWebSocketService.sendAuthorized({ chatType, lastKey, tags });
    },
    [],
  );

  const handleStateChange = useCallback(
    (newState: TConnectionState) => {
      const store = useChatStore.getState();
      setConnectionState(newState);

      switch (newState) {
        case "connecting":
          store.setWebSocketLoading();
          break;
        case "connected":
          store.setWebSocketFulfilled();
          fetchConversations("to", undefined, DEFAULT_TAGS);
          fetchConversations("from", undefined, null);
          // Contador das badges das abas via action dedicada (total real). ANCR-5329.
          chatWebSocketService.requestUnreadCount();
          break;
        case "error":
          store.setWebSocketRejected();
          break;
        case "closed":
          store.setWebSocketClosed();
          break;
        case "idle":
        default:
          break;
      }
    },
    [fetchConversations],
  );

  useEffect(() => {
    chatWebSocketService.onMessage(
      [WS_ACTIONS.getChats],
      (message: TWsMessage) => {
        const store = useChatStore.getState();
        const { chatType, data, lastKey, unreadChats } =
          message as TWsGetChatsResponse;

        chatWebSocketService.decrementPendingRequestCount(chatType);
        chatWebSocketService.setPendingResponseData(chatType, {
          data,
          lastKey,
          unreadChats,
        });

        if (chatWebSocketService.getPendingRequestCount(chatType) === 0) {
          const responseData = chatWebSocketService.getPendingResponseData(chatType);
          const conversations = (responseData?.data ?? []) as TConversation[];
          const direction: "from" | "to" = chatType === "to" ? "to" : "from";

          store.setConversationListFulfilled(direction, conversations, chatType);
          store.setConversationStatus({
            chatType: direction,
            empty: !conversations.length,
            hasOlderChats: (responseData?.lastKey as TPaginationKey & { id?: string | number })?.id
              ? (responseData?.lastKey as TPaginationKey)
              : undefined,
            status: API_REQUEST_STATUS.ready,
            // Badge é dona da action getUnreadCount (total real); preservamos o
            // valor atual aqui para não sobrescrever com o parcial do getChats. ANCR-5329.
            unreadChats: useChatStore.getState().conversation?.unreadChats?.[direction] ?? 0,
          });
          chatWebSocketService.setPendingResponseData(chatType, null);
        }
      },
    );

    chatWebSocketService.onMessage(
      [
        WS_ACTIONS.getMessages,
        WS_ACTIONS.sendMessage,
        WS_ACTIONS.unreadMessage,
        WS_ACTIONS.statusMessage,
        WS_ACTIONS.getPhones,
      ],
      (message: TWsMessage) => {
        const store = useChatStore.getState();

        switch (message.action) {
          case WS_ACTIONS.getMessages: {
            const { chatId, data, lastKey, actualLead } = message as TWsGetMessagesResponse;
            // Experimento ANCR-5329: o servidor marca a conversa como lida ao retornar o
            // getMessages → pedimos o total fresco logo após (badge das abas) e avisamos
            // o Header (socket próprio) pra ele também re-buscar e acompanhar a leitura.
            chatWebSocketService.requestUnreadCount();
            notifyHeaderUnreadChanged();
            if (!data?.length) break;
            store.setMessageListFulfilled({
              chatId,
              messages: data,
              hasOlderMessagesToFetch: (lastKey as TPaginationKey & { id?: string | number })?.id
                ? lastKey
                : false,
              actualLead: actualLead as Parameters<
                typeof store.setMessageListFulfilled
              >[0]["actualLead"],
            });
            break;
          }

          case WS_ACTIONS.getPhones: {
            const { id, data } = message as TWsGetPhonesResponse;
            const phone = data?.residentialPhone || data?.mobilePhone;
            store.setPhoneFulfilled(id, phone ?? '');
            break;
          }

          case WS_ACTIONS.sendMessage: {
            const { data, chatId } = message as TWsSendMessageResponse;
            store.setNewMessageFulfilled({
              messages: [data],
              chatId,
              chatType: "",
              itsMyMessage: false,
            });
            break;
          }

          case WS_ACTIONS.unreadMessage: {
            const { data } = message as TWsUnreadMessageResponse;
            store.setMessageUnreadFulfilled(
              data as Parameters<typeof store.setMessageUnreadFulfilled>[0],
            );
            break;
          }

          case WS_ACTIONS.statusMessage: {
            const { updatedMessages } = message as TWsStatusMessageResponse;
            store.setMessageStatusFulfilled(
              updatedMessages as Parameters<
                typeof store.setMessageStatusFulfilled
              >[0],
            );
            break;
          }

          default:
            break;
        }

        // getUnreadCount é pull. Gatilho: SÓ o `unreadMessage` — o back confirmou que é
        // o evento autônomo (push) emitido quando o total de não-lidas muda, e nesse
        // momento o dado está fresco. Antes pedíamos também em sendMessage/statusMessage,
        // que chegavam cedo/defasados (statusMessage era o suspeito do retorno antigo).
        // O decremento otimista no clique segue dando o feedback instantâneo na leitura.
        // Experimento ANCR-5329.
        if (message.action === WS_ACTIONS.unreadMessage) {
          chatWebSocketService.requestUnreadCount();
        }
      },
    );

    chatWebSocketService.onMessage(
      [WS_ACTIONS.updateBlockUser],
      (message: TWsMessage) => {
        const { success, blocked, chatId } = message as TWsBlockUserResponse;

        if (!success) return;

        const store = useChatStore.getState();
        store.blockUserConversation({ chatId, blocked });
        store.blockUserConversationList("to", { chatId, blocked });
        store.blockUserConversationList("from", { chatId, blocked });

        if (blocked) {
          store.resetConversationActive();
        } else {
          store.setMessageListLoading(chatId);
          chatWebSocketService.send({
            action: WS_ACTIONS.getMessages,
            chatId,
            limit: WS_FETCH_LIMIT,
          });
        }

        const currentState = useChatStore.getState();
        const activeChatType = currentState?.conversation?.chatType ?? "to";
        const selectedTags = currentState?.conversation?.selectedTags ?? [];
        const direction: "from" | "to" = activeChatType === "from" ? "from" : "to";

        chatWebSocketService.resetPendingRequestCount(activeChatType);
        chatWebSocketService.incrementPendingRequestCount(activeChatType);

        store.resetConversationList(direction);
        store.setConversationListLoading(direction, activeChatType);

        let tagsToFetch: string[] | null =
          selectedTags.length > 0 ? selectedTags : null;
        if (activeChatType === "to" && selectedTags.length === 0) {
          tagsToFetch = DEFAULT_TAGS;
        }

        chatWebSocketService.sendAuthorized({
          chatType: activeChatType,
          lastKey: undefined,
          tags: tagsToFetch,
        });
      },
    );

    // Contador das badges das abas: total real por perspectiva via action
    // dedicada getUnreadCount (substitui o unreadChats parcial do getChats). ANCR-5329.
    chatWebSocketService.onMessage(
      [WS_ACTIONS.getUnreadCount],
      (message: TWsMessage) => {
        const { unreadChatsFrom, unreadChatsTo } =
          message as TWsGetUnreadCountResponse;
        useChatStore.getState().setUnreadChatsCount({
          from: unreadChatsFrom ?? 0,
          to: unreadChatsTo ?? 0,
        });
      },
    );
  }, []);

  useEffect(() => {
    if (!enabled || hasConnected.current) return;
    hasConnected.current = true;

    const token = jwtToken();
    const url = token && `${import.meta.env.VITE_CHAT_WEB_SOCKET}?v2=true`;
    if (!url) return;

    chatWebSocketService.onStateChange(handleStateChange);
    chatWebSocketService.connect(url, token);

    return () => {
      hasConnected.current = false;
      chatWebSocketService.close();
    };
  }, [enabled, handleStateChange]);

  return { connectionState };
}
